import React, { useState, useEffect } from 'react';

export type CookieConsentContextType = {
  consentActiveGroups: string[];
  hasConsentFor: (group: string) => boolean;
};

export const CookieConsentContext =
  React.createContext<CookieConsentContextType>({
    consentActiveGroups: [],
    hasConsentFor: null,
  });

const getActiveGroups = () => {
  let activeGroups = '';

  if (typeof window !== 'undefined') {
    activeGroups = window['OnetrustActiveGroups'] || '';
  }

  return activeGroups?.split(',')?.filter((group) => !!group);
};

export const CookieConsentProvider: React.FC = ({ children }) => {
  const [consentActiveGroups, setConsentActiveGroups] = useState(
    getActiveGroups(),
  );

  useEffect(() => {
    const initialGroups = getActiveGroups();
    setConsentActiveGroups(initialGroups);

    const handleConsentApplied = () => {
      const currentGroups = getActiveGroups();

      if (currentGroups !== initialGroups) {
        setConsentActiveGroups(currentGroups);
        setTimeout(() => {
          window.location.reload();
        }, 200);
      }
    };

    // Improved "OnConsentChanged" callback since it fires after consent has been recorded back to OT.
    // https://developer.onetrust.com/onetrust/docs/javascript-events-guide#otconsentapplied
    window.addEventListener('OTConsentApplied', handleConsentApplied);

    return () => {
      window.removeEventListener('OTConsentApplied', handleConsentApplied);
    };
  }, []);

  const hasConsentFor = (group: string) => {
    return consentActiveGroups?.includes(group);
  };

  return (
    <CookieConsentContext.Provider
      value={{ consentActiveGroups, hasConsentFor }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
